import type { PrefsHandle } from './webledgerPrefs.js';

/**
 * Where you were scrolled to on a list — legacy's `trackScroll`/`restoreScroll`.
 *
 * The marker in `markCurrentRow` and this are two halves of the same answer
 * to "where was I", and neither replaces the other. The marker says *which
 * row*; this says *what you could see around it*. Coming back to a 400-row
 * register centred on one row, with everything above and below it different
 * from what you left, is disorienting in a way that landing exactly where you
 * were is not.
 *
 * Stored in prefs rather than in the browser, for the same reason every other
 * webledger preference moved there: a position is a fact about the reader, not
 * about the machine. It is also the reason for the debounce — every stop
 * writes, and writing on every scroll *event* would be hundreds of requests
 * for one flick of a trackpad.
 *
 * Two things are deliberately not persisted: a horizontal position (no list
 * here scrolls sideways as a matter of course), and anything at all when the
 * page never scrolled. A stored `0` is indistinguishable from "never looked",
 * and restoring it would be a no-op either way.
 *
 * What is recorded is where the *reader* went, which is not always where the
 * window is — see `readerY`. Replacing a list's markup clamps the window to
 * the top, and treating that as a scroll is how a "remember my place" feature
 * turns into one that forgets it every time you touch a filter.
 */

/** Legacy's 500ms. Long enough that a flick writes once, short enough that a glance still counts. */
const SETTLE_MS = 500;

export interface ScrollTracker {
  /**
   * Begin recording. A tracker is created listening but idle, and stays idle
   * until the page says it has finished arriving — after a `restoreScroll`,
   * or after deciding not to restore.
   *
   * Not merged into `trackScroll` because the gap between the two is the
   * whole point: a list that is still loading is a few hundred pixels tall,
   * and anything that scrolls it in that window (a stray wheel event, the
   * restore itself) would record a position the reader never chose, on top
   * of the one they are about to be given back.
   */
  start(): void;
  /**
   * Ignore scrolling for the next couple of frames. Call it immediately
   * *after* replacing a list's markup.
   *
   * Emptying a list clamps the window to whatever the new content can hold —
   * usually the top — and the browser reports that as an ordinary scroll.
   * Saved, it overwrites the reader's real position with `0`: switching to
   * another report and back, or narrowing a filter, would quietly throw away
   * where they had been.
   *
   * Self-expiring rather than paired with a `resume`, deliberately. A render
   * that returns early — nothing to show, the wrong report, an offline read
   * that failed — is exactly the kind of path that forgets to switch tracking
   * back on, and the failure would be silent and permanent for that page.
   */
  settling(): void;
  /**
   * Point this tracker at a different key from now on.
   *
   * For a page that scrolls one window through several lists in turn — the
   * wallet log is one per wallet, and switching wallet does not reload the
   * page. The position held for the OLD key is written before the switch, so
   * walking away from a wallet keeps your place in it; the tracker then goes
   * idle until `start` is called again, because the new list has not been
   * drawn yet and anything recorded before it is a position nobody chose.
   */
  key(next: string): void;
}

/**
 * Watch `window.scrollY` under `key`, saving it whenever scrolling settles.
 *
 * Returns idle: nothing is recorded until `start()`. See that method.
 *
 * `active` gates saving, for a page whose views share one scroll area — the
 * reports page scrolls the same window for three different reports, and only
 * one of them has a position worth remembering.
 *
 * The two flushes are not belt-and-braces. A debounced write alone loses the
 * last half-second of scrolling every time, and the last half-second is the
 * one that matters: it is where the reader stopped before clicking away.
 * `pagehide` covers a navigation, `visibilitychange` covers a phone being
 * locked or a tab being switched, which on mobile is often all that ever
 * fires.
 */
/**
 * What is actually scrolling.
 *
 * The window, for a page that scrolls as a whole, and an element for a list
 * that scrolls inside a box of its own — the wallet log is the second kind,
 * with twenty-five thousand pixels of rows inside a pane under a fixed
 * header, and a window-based tracker watching it records nothing at all
 * because the window never moves.
 */
export interface Scroller {
  top(): number;
  to(y: number): void;
  listen(fn: () => void): void;
}

export const windowScroller: Scroller = {
  top: () => window.scrollY,
  to: (y) => window.scrollTo({ top: y, behavior: 'instant' }),
  listen: (fn) => window.addEventListener('scroll', fn, { passive: true }),
};

export function elementScroller(el: HTMLElement): Scroller {
  return {
    top: () => el.scrollTop,
    to: (y) => { el.scrollTop = y; },
    listen: (fn) => el.addEventListener('scroll', fn, { passive: true }),
  };
}

export function trackScroll(
  prefs: PrefsHandle, initialKey: string, active?: () => boolean,
  scroller: Scroller = windowScroller,
): ScrollTracker {
  let key = initialKey;
  const enabled = (): boolean => started && !paused && (active ? active() : true);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let started = false;
  let paused = false;
  let settleToken = 0;
  /**
   * The last position the *reader* put us at.
   *
   * Not `window.scrollY`, and the difference is the whole of what makes this
   * survive a re-render. Emptying a list leaves the window genuinely at the
   * top, so a flush on the way out would record `0` however carefully the
   * clamp's own event was suppressed — and the reader's place, which they
   * never left, would be gone. Only scrolling that gets past `enabled` moves
   * this, so a clamp writes nothing and a real scroll to the top writes `0`
   * as it should.
   */
  let readerY = 0;

  const save = (keepalive: boolean): void => {
    if (!enabled()) return;
    prefs.set(key, String(Math.round(readerY)), keepalive ? { keepalive: true } : {});
  };
  const onScroll = (): void => {
    if (!enabled()) return;
    readerY = scroller.top();
    clearTimeout(timer);
    timer = setTimeout(() => save(false), SETTLE_MS);
  };
  const onPageHide = (): void => save(true);
  const onVisibility = (): void => { if (document.visibilityState === 'hidden') save(true); };

  scroller.listen(onScroll);
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibility);

  return {
    start() {
      started = true;
      // Wherever the page settled — a restored position, or the top.
      readerY = scroller.top();
    },
    key(next: string) {
      if (next === key) return;
      // The old list's position, written before the key moves off it.
      save(false);
      clearTimeout(timer);
      key = next;
      started = false;
      readerY = 0;
    },
    settling() {
      paused = true;
      clearTimeout(timer);
      // Two frames, for the same reason `restoreScroll` waits two: a clamp
      // has not necessarily been reported by the time the markup is
      // assigned. A second call inside the window extends it rather than
      // ending it early, which is what `token` is checking.
      const mine = (settleToken += 1);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (settleToken === mine) paused = false;
      }));
    },
  };
}

/**
 * Put the window back where `key` says it was, resolving once it is done.
 *
 * Two frames of delay, because a position restored before the browser has
 * laid the list out is clamped to whatever the page's height happens to be at
 * that instant — usually near zero, which silently turns "back where I was"
 * into "back at the top". Legacy waits the same two frames.
 *
 * Awaiting the result is what lets a caller decide the precedence between
 * this and a row it also wants to point at: restore first, and then only
 * scroll to the row if it is *still* not on screen. Two things moving the
 * page in the same frame is a fight the reader watches.
 */
export async function restoreScroll(
  prefs: PrefsHandle, key: string, scroller: Scroller = windowScroller,
): Promise<void> {
  const saved = Number(prefs.get(key) ?? 0);
  if (!Number.isFinite(saved) || saved <= 0) return;
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      scroller.to(saved);
      resolve();
    }));
  });
}
